import * as XLSX from "xlsx";
import Papa from "papaparse";

/**
 * Formats payment data for export with localized column headers
 * @param {Array} rows - Array of payment row objects
 * @param {string} language - Current language ('am', 'ru', 'en')
 * @param {Function} getTranslation - Translation function
 * @returns {Array} Formatted data array
 */
export const formatPaymentDataForExport = (rows, language, getTranslation) => {
    if (!Array.isArray(rows) || rows.length === 0) {
        return [];
    }

    return rows.map((item) => {
        return {
            [getTranslation('payments.orderNumber', language)]: item?.orderNumber || '',
            [getTranslation('payments.amount', language)]: item?.totalPrice ?? 0,
            [getTranslation('payments.platformFee', language)]: item?.platformFee ?? 0,
            [getTranslation('payments.chefEarning', language)]: item?.chefEarning ?? 0,
            [getTranslation('payments.paymentType', language)]: item?.paymentType || '',
            [getTranslation('payments.promoCodeApplied', language)]: item?.promoCodeApplied 
                ? getTranslation('common.yes', language) 
                : getTranslation('common.no', language)
        };
    });
};

/**
 * Exports payment data to CSV format
 * @param {Array} payments - Array of payment objects
 * @param {string} dateFrom - Start date (YYYY-MM-DD)
 * @param {string} dateTo - End date (YYYY-MM-DD)
 * @param {string} language - Current language
 * @param {Function} getTranslation - Translation function
 */
export const exportPaymentsToCSV = (payments, dateFrom, dateTo, language, getTranslation) => {
    if (!payments || payments.length === 0) {
        throw new Error('No payment data to export');
    }

    // Format data for export
    const formattedData = formatPaymentDataForExport(payments, language, getTranslation);

    // Convert to CSV
    const csv = Papa.unparse(formattedData);

    // Create blob and download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `payments_report_${dateFrom}_to_${dateTo}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
};

/**
 * Exports payment data to Excel format
 * @param {Array} payments - Array of payment objects
 * @param {string} dateFrom - Start date (YYYY-MM-DD)
 * @param {string} dateTo - End date (YYYY-MM-DD)
 * @param {string} language - Current language
 * @param {Function} getTranslation - Translation function
 */
export const exportPaymentsToExcel = (payments, dateFrom, dateTo, language, getTranslation) => {
    if (!payments || payments.length === 0) {
        throw new Error('No payment data to export');
    }

    // Format data for export
    const formattedData = formatPaymentDataForExport(payments, language, getTranslation);

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(formattedData);

    // Set column widths for better readability
    const colWidths = [
        { wch: 15 }, // Order Number
        { wch: 12 }, // Amount
        { wch: 12 }, // Platform Fee
        { wch: 12 }, // Chef Earning
        { wch: 15 }, // Payment Type
        { wch: 15 }  // Promo Code Applied
    ];
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Payments');

    // Generate Excel file and download
    XLSX.writeFile(wb, `payments_report_${dateFrom}_to_${dateTo}.xlsx`);
};
